import { getTableSorting } from "@/utils/tableUtils";
import { dateToIsoDateString, getClosestPreviousWorkDay, isDefined, toDate } from "@/utils/utils";
import { TablePaginationConfig } from "antd";
import { SorterResult } from "antd/es/table/interface";
import dayjs, { Dayjs } from "dayjs";
import { Permission } from "../../common/security/enums";
import { hasAllPermissions, hasPermission } from "../../common/security/utils";
import { FieldConstraintViolation } from "../../common/types";
import { CalcType } from "../calculator/enums";
import { ClientFormType } from "../client/enums";
import { Client } from "../client/types";
import { ClientDataProps, ClientsWithFormType, createClientsData } from "../client/utils";
import { ContractFormSearchContract } from "../contractform/types";
import { AgentUserAccount } from "../user/types";
import {
  ContractOrderByColumn,
  ContractStatus,
  ContractType,
  ContractVerificationStatus,
  InsurancePeriod,
  InsuranceType,
  PeriodType,
} from "./enums";
import {
  BaseVehicleInsurance,
  Contract,
  ContractFilterPageRequest,
  ContractGainerRecord,
  ContractList,
  CreateUpdateContractGainerRecord,
  DepositContract,
  GenericContract,
  GenericInsurance,
  Insurance,
  InsuranceContract,
  InvestmentContract,
  LifeInsurance,
  LoanContract,
  RealtyInsurance,
  SecondPillarContract,
  StandardVehicleInsurance,
  ThirdPillarContract,
  TravelInsurance,
  VehicleInsurance,
} from "./types";

export const INSURANCE_CONTRACT_CANCELLING_TERM_DAYS = 42;
const LOAN_CONTRACT_FIXATION_TERM_MONTHS = 3;

export const VEHICLE_INSURANCE_TYPES = [InsuranceType.MTPL, InsuranceType.CRASH, InsuranceType.GAP, InsuranceType.PAS];

type ContractPermissions = {
  create: Permission;
  read: Permission;
  update: Permission;
  limitedUpdate: Permission;
  delete: Permission;
  ownVerificationSend: Permission;
  directorateVerificationSend: Permission;
  verificationStatusChange: Permission;
  privilegedChangesOnVerified: Permission;
};

export const contractPermissionsByContractType = {
  [ContractType.DEPOSIT_CONTRACT]: {
    create: Permission.DEPOSIT_CREATE,
    read: Permission.DEPOSIT_READ,
    update: Permission.DEPOSIT_UPDATE,
    limitedUpdate: Permission.DEPOSIT_LIMITED_UPDATE,
    delete: Permission.DEPOSIT_DELETE,
    ownVerificationSend: Permission.DEPOSIT_OWN_VERIFICATION_SEND,
    directorateVerificationSend: Permission.DEPOSIT_DIRECTORATE_VERIFICATION_SEND,
    verificationStatusChange: Permission.DEPOSIT_VERIFICATION_STATUS_CHANGE,
    privilegedChangesOnVerified: Permission.PRIVILEGED_CHANGES_ON_VERIFIED_DEPOSIT,
  },
  [ContractType.GENERIC_CONTRACT]: {
    create: Permission.GENERIC_CREATE,
    read: Permission.GENERIC_READ,
    update: Permission.GENERIC_UPDATE,
    limitedUpdate: Permission.GENERIC_LIMITED_UPDATE,
    delete: Permission.GENERIC_DELETE,
    ownVerificationSend: Permission.GENERIC_OWN_VERIFICATION_SEND,
    directorateVerificationSend: Permission.GENERIC_DIRECTORATE_VERIFICATION_SEND,
    verificationStatusChange: Permission.GENERIC_VERIFICATION_STATUS_CHANGE,
    privilegedChangesOnVerified: Permission.PRIVILEGED_CHANGES_ON_VERIFIED_GENERIC,
  },
  [ContractType.INSURANCE_CONTRACT]: {
    create: Permission.INSURANCE_CREATE,
    read: Permission.INSURANCE_READ,
    update: Permission.INSURANCE_UPDATE,
    limitedUpdate: Permission.INSURANCE_LIMITED_UPDATE,
    delete: Permission.INSURANCE_DELETE,
    ownVerificationSend: Permission.INSURANCE_OWN_VERIFICATION_SEND,
    directorateVerificationSend: Permission.INSURANCE_DIRECTORATE_VERIFICATION_SEND,
    verificationStatusChange: Permission.INSURANCE_VERIFICATION_STATUS_CHANGE,
    privilegedChangesOnVerified: Permission.PRIVILEGED_CHANGES_ON_VERIFIED_INSURANCE,
  },
  [ContractType.INVESTMENT_CONTRACT]: {
    create: Permission.INVESTMENT_CREATE,
    read: Permission.INVESTMENT_READ,
    update: Permission.INVESTMENT_UPDATE,
    limitedUpdate: Permission.INVESTMENT_LIMITED_UPDATE,
    delete: Permission.INVESTMENT_DELETE,
    ownVerificationSend: Permission.INVESTMENT_OWN_VERIFICATION_SEND,
    directorateVerificationSend: Permission.INVESTMENT_DIRECTORATE_VERIFICATION_SEND,
    verificationStatusChange: Permission.INVESTMENT_VERIFICATION_STATUS_CHANGE,
    privilegedChangesOnVerified: Permission.PRIVILEGED_CHANGES_ON_VERIFIED_INVESTMENT,
  },
  [ContractType.LOAN_CONTRACT]: {
    create: Permission.LOAN_CREATE,
    read: Permission.LOAN_READ,
    update: Permission.LOAN_UPDATE,
    limitedUpdate: Permission.LOAN_LIMITED_UPDATE,
    delete: Permission.LOAN_DELETE,
    ownVerificationSend: Permission.LOAN_OWN_VERIFICATION_SEND,
    directorateVerificationSend: Permission.LOAN_DIRECTORATE_VERIFICATION_SEND,
    verificationStatusChange: Permission.LOAN_VERIFICATION_STATUS_CHANGE,
    privilegedChangesOnVerified: Permission.PRIVILEGED_CHANGES_ON_VERIFIED_LOAN,
  },
  [ContractType.SECOND_PILLAR_CONTRACT]: {
    create: Permission.SECOND_PILLAR_CREATE,
    read: Permission.SECOND_PILLAR_READ,
    update: Permission.SECOND_PILLAR_UPDATE,
    limitedUpdate: Permission.SECOND_PILLAR_LIMITED_UPDATE,
    delete: Permission.SECOND_PILLAR_DELETE,
    ownVerificationSend: Permission.SECOND_PILLAR_OWN_VERIFICATION_SEND,
    directorateVerificationSend: Permission.SECOND_PILLAR_DIRECTORATE_VERIFICATION_SEND,
    verificationStatusChange: Permission.SECOND_PILLAR_VERIFICATION_STATUS_CHANGE,
    privilegedChangesOnVerified: Permission.PRIVILEGED_CHANGES_ON_VERIFIED_SECOND_PILLAR,
  },
  [ContractType.THIRD_PILLAR_CONTRACT]: {
    create: Permission.THIRD_PILLAR_CREATE,
    read: Permission.THIRD_PILLAR_READ,
    update: Permission.THIRD_PILLAR_UPDATE,
    limitedUpdate: Permission.THIRD_PILLAR_LIMITED_UPDATE,
    delete: Permission.THIRD_PILLAR_DELETE,
    ownVerificationSend: Permission.THIRD_PILLAR_OWN_VERIFICATION_SEND,
    directorateVerificationSend: Permission.THIRD_PILLAR_DIRECTORATE_VERIFICATION_SEND,
    verificationStatusChange: Permission.THIRD_PILLAR_VERIFICATION_STATUS_CHANGE,
    privilegedChangesOnVerified: Permission.PRIVILEGED_CHANGES_ON_VERIFIED_THIRD_PILLAR,
  },
} satisfies Record<ContractType, ContractPermissions>;

type ParseVehicleModelNameData = {
  customBrand?: string;
  brandName: string;
  customModel?: string;
  modelName: string;
};

export const parseVehicleModelName = ({
  customBrand,
  brandName,
  modelName,
  customModel,
}: ParseVehicleModelNameData): string => {
  return (customBrand || brandName) + " " + (customModel || modelName);
};

type CalculateContactStatusMethodType = {
  startDate: string;
  endDate?: string;
  cancellationDate?: string;
  transferredToOtherBrokerDate?: string;
};

export const calculateContractStatus = ({
  startDate,
  endDate,
  cancellationDate,
  transferredToOtherBrokerDate,
}: CalculateContactStatusMethodType): ContractStatus | undefined => {
  const today = dayjs();
  if (toDate(transferredToOtherBrokerDate)?.isBefore(today, "day")) {
    return ContractStatus.TRANSFERRED_TO_BROKER;
  }
  if (toDate(cancellationDate)?.isBefore(today, "day")) {
    return ContractStatus.CANCELED;
  }
  if (toDate(endDate)?.isBefore(today, "day")) {
    return ContractStatus.FINISHED;
  }
  return startDate
    ? toDate(startDate)?.isSameOrBefore(today, "day")
      ? ContractStatus.ACTIVE
      : ContractStatus.UNSTARTED
    : undefined;
};

export const calculateGainerRecordStartDates = (
  records: CreateUpdateContractGainerRecord[],
  contractStartDate?: string,
): CreateUpdateContractGainerRecord[] =>
  records.map((record, index) => {
    if (index === 0) {
      return contractStartDate ? { ...record, startDate: contractStartDate } : record;
    }

    const previousRecordEndDate = toDate(records[index - 1]?.endDate);

    return previousRecordEndDate
      ? { ...record, startDate: dateToIsoDateString(previousRecordEndDate.add(1, "day")) }
      : record;
  });

/**
 * Calculate insurance period end date (KPO)
 */
export const calculateInsurancePeriodEndDate = (
  cancellationDate: string,
  periodType: PeriodType,
  effectiveEndDate: string | undefined,
  insurancePeriod: InsurancePeriod,
  effectiveBeginningDate: string,
  transferredToOtherBrokerDate: string | undefined,
  status?: ContractStatus,
): string | undefined => {
  if (cancellationDate) {
    return dateToIsoDateString(dayjs(cancellationDate));
  }

  if (periodType) {
    switch (periodType) {
      case PeriodType.DEFINITE:
        if (effectiveEndDate) {
          return dateToIsoDateString(dayjs(effectiveEndDate));
        }
        break;
      case PeriodType.INDEFINITE:
        if (insurancePeriod && effectiveBeginningDate) {
          switch (insurancePeriod) {
            case InsurancePeriod.CALENDAR_YEAR:
              const d = toDate(effectiveBeginningDate)?.isAfter(dayjs(), "day")
                ? dayjs(effectiveBeginningDate)
                : dayjs();
              if (status) {
                if (status === ContractStatus.TRANSFERRED_TO_BROKER) {
                  if (transferredToOtherBrokerDate) {
                    const iped = d.endOf("year");
                    if (toDate(transferredToOtherBrokerDate)?.isSameOrBefore(iped, "day")) {
                      return dateToIsoDateString(iped.year(toDate(transferredToOtherBrokerDate)?.year() as number));
                    } else {
                      return dateToIsoDateString(
                        iped.year(toDate(transferredToOtherBrokerDate)?.year() as number).add(1, "year"),
                      );
                    }
                  }
                } else {
                  return dateToIsoDateString(d.endOf("year"));
                }
              }
              break;
            case InsurancePeriod.TECHNICAL_YEAR:
              if ((toDate(effectiveBeginningDate)?.year() as number) >= dayjs().year()) {
                return dateToIsoDateString(dayjs(effectiveBeginningDate).add(1, "year").subtract(1, "day"));
              } else {
                if (status) {
                  if (status === ContractStatus.TRANSFERRED_TO_BROKER) {
                    if (transferredToOtherBrokerDate) {
                      const z = dayjs(effectiveBeginningDate)
                        .year(toDate(transferredToOtherBrokerDate)?.year() as number)
                        .subtract(1, "day");
                      if (z.isBefore(transferredToOtherBrokerDate, "day")) {
                        return dateToIsoDateString(z.add(1, "year"));
                      } else {
                        return dateToIsoDateString(z);
                      }
                    }
                  } else {
                    const z = dayjs(effectiveBeginningDate).year(dayjs().year()).subtract(1, "day");
                    if (z.isBefore(dayjs(), "day")) {
                      return dateToIsoDateString(z.add(1, "year"));
                    } else {
                      return dateToIsoDateString(z);
                    }
                  }
                }
              }
              break;
          }
        }
        break;
    }
  }

  return undefined;
};

/**
 * Calculate the last date for contract cancellation (PDnVZ)
 */
export const calculateLastContractCancellationDate = (
  periodType: PeriodType,
  cancellationDate: string,
  transferredToOtherBrokerDate: string | undefined,
  effectiveBeginningDate: string,
  insurancePeriodEndDate?: string,
): string | undefined => {
  if (periodType) {
    switch (periodType) {
      case PeriodType.DEFINITE:
        return undefined;
      case PeriodType.INDEFINITE:
        if (cancellationDate) {
          return undefined;
        } else if (transferredToOtherBrokerDate) {
          return undefined;
        } else if (effectiveBeginningDate && insurancePeriodEndDate) {
          const d = toDate(effectiveBeginningDate)?.isAfter(dayjs(), "day") ? dayjs(effectiveBeginningDate) : dayjs();
          const k = getClosestPreviousWorkDay(
            dayjs(insurancePeriodEndDate).subtract(INSURANCE_CONTRACT_CANCELLING_TERM_DAYS, "day"),
          );

          if (k.isSameOrAfter(d, "day")) {
            return dateToIsoDateString(k);
          } else {
            return dateToIsoDateString(
              getClosestPreviousWorkDay(
                dayjs(insurancePeriodEndDate).add(1, "year").subtract(INSURANCE_CONTRACT_CANCELLING_TERM_DAYS, "day"),
              ),
            );
          }
        }
        break;
    }
  }

  return undefined;
};

export const calculateContactClientDate = (fixationAnniversaryDate?: Dayjs): string | undefined => {
  return fixationAnniversaryDate
    ? dateToIsoDateString(
        getClosestPreviousWorkDay(dayjs(fixationAnniversaryDate).subtract(LOAN_CONTRACT_FIXATION_TERM_MONTHS, "month")),
      )
    : undefined;
};

/**
 * The API accepts sending from SENT as well, but that is offered as the note edit of an already sent contract
 * (see ContractTags), not as the send action.
 */
export const VERIFICATION_STATUSES_FOR_SENDING_TO_BACKOFFICE = [
  ContractVerificationStatus.TYPED,
  ContractVerificationStatus.INTERNAL_INTERVENTION,
  ContractVerificationStatus.EXTERNAL_INTERVENTION,
  ContractVerificationStatus.INTERNAL_AND_EXTERNAL_INTERVENTION,
];

export const canSendContractToBackoffice = (account: AgentUserAccount, contract: Contract | ContractList): boolean => {
  const permissions = contractPermissionsByContractType[contract.type];

  const ids =
    contract.gainerRecords
      ?.map((r) => [r.gainer1?.id, r.gainer2?.id, r.gainer3?.id, r.gainer4?.id, r.gainer5?.id].filter((id) => !!id))
      .flat() ?? [];

  const allAgentsIds = [contract.signer?.id, contract.manager?.id, ...ids];

  const isAgentOnContract =
    allAgentsIds.includes(account.agent?.id) || allAgentsIds.includes(account.representingAgent?.id);

  const isSignerFromAgentDirectorate = contract.signerDirectorateId === account.agent?.directorate?.id;

  return (
    hasPermission(account.permissions, permissions.verificationStatusChange) ||
    (isAgentOnContract && hasPermission(account.permissions, permissions.ownVerificationSend)) ||
    (isSignerFromAgentDirectorate && hasPermission(account.permissions, permissions.directorateVerificationSend))
  );
};

export const isContractVerified = (contract?: Contract): boolean => {
  return (
    contract?.verificationStatus === ContractVerificationStatus.VERIFIED ||
    contract?.verificationStatus === ContractVerificationStatus.PAID
  );
};

export const hasVerificationExportTogglePermissions = (
  contractType: ContractType,
  permissions: Permission[],
): boolean => {
  return hasAllPermissions(permissions, [
    contractPermissionsByContractType[contractType].read,
    Permission.CONTRACT_VERIFICATION_EXPORT,
  ]);
};

export const getClientsDataFromInsuranceContract = (contract: InsuranceContract): ClientDataProps[] | undefined => {
  if (contract.type !== ContractType.INSURANCE_CONTRACT) {
    return undefined;
  }

  const clientsWithFromType: ClientsWithFormType = [
    { type: ClientFormType.POLICY_HOLDER, client: contract.clients[contract.policyHolderIndex] },
  ];

  contract.insurances.forEach((insurance) => {
    const vinculationClientIndex = (insurance as RealtyInsurance).vinculationClientIndex;

    switch (insurance.type) {
      case InsuranceType.MTPL:
      case InsuranceType.CRASH:
      case InsuranceType.GAP:
      case InsuranceType.PAS:
      case InsuranceType.VEHICLE:
        clientsWithFromType.push({
          type: ClientFormType.HOLDER,
          client: contract.clients[(insurance as BaseVehicleInsurance).vehicleHolderIndex],
        });
        clientsWithFromType.push({
          type: ClientFormType.OWNER,
          client: contract.clients[(insurance as BaseVehicleInsurance).vehicleOwnerIndex],
        });
        break;
      case InsuranceType.REALTY:
        clientsWithFromType.push({
          type: ClientFormType.INSURED,
          client: contract.clients[(insurance as RealtyInsurance).insuredClientIndex],
        });
        clientsWithFromType.push({
          type: ClientFormType.VINCULATION,
          client: isDefined(vinculationClientIndex) ? contract.clients[vinculationClientIndex] : undefined,
        });
        break;
      case InsuranceType.LIFE:
        clientsWithFromType.push({
          type: ClientFormType.INSURED,
          client: contract.clients[(insurance as LifeInsurance).insuredClientIndex],
        });
        break;
      case InsuranceType.GENERIC:
        clientsWithFromType.push({
          type: ClientFormType.INSURED,
          client: contract.clients[(insurance as GenericInsurance).insuredClientIndex],
        });
        break;
      default:
        break;
    }
  });

  return createClientsData(clientsWithFromType);
};

export const handleTableChange = (
  pagination: TablePaginationConfig,
  __: Record<string, any>,
  sorter: SorterResult<ContractList> | SorterResult<ContractList>[],
  updateSearchParams: (searchParams: ContractFilterPageRequest, append?: boolean, replace?: boolean) => void,
) => {
  const { orderBy, orderDirections } = getTableSorting<ContractOrderByColumn>(sorter);

  updateSearchParams(
    { orderBy, orderDirections, pageIndex: pagination.current ? pagination.current - 1 : undefined },
    true,
    false,
  );
};

export const convertInsuranceTypeToCalcType = (insurance: Insurance): CalcType | undefined => {
  if (isVehicleInsurance(insurance)) {
    if (insurance.type === InsuranceType.MTPL) {
      return CalcType.MTPL;
    } else if (insurance.type === InsuranceType.CRASH) {
      return CalcType.CRASH;
    } else if (insurance.type === InsuranceType.GAP) {
      return CalcType.GAP;
    } else if (insurance.type === InsuranceType.PAS) {
      return CalcType.PAS;
    }
  } else if (isRealtyInsurance(insurance)) {
    return CalcType.REALTY;
  } else if (isTravelInsurance(insurance)) {
    return CalcType.TRAVEL;
  }

  return undefined;
};

export const isTravelInsurance = (insurance: Insurance): insurance is TravelInsurance =>
  insurance.type === InsuranceType.TRAVEL;

export const isRealtyInsurance = (insurance: Insurance): insurance is RealtyInsurance =>
  insurance.type === InsuranceType.REALTY;

export const isVehicleInsurance = (insurance: Insurance): insurance is StandardVehicleInsurance =>
  insurance.type === InsuranceType.MTPL ||
  insurance.type === InsuranceType.CRASH ||
  insurance.type === InsuranceType.PAS ||
  insurance.type === InsuranceType.GAP;

export const isOtherVehicleInsurance = (insurance: Insurance): insurance is VehicleInsurance =>
  insurance.type === InsuranceType.VEHICLE;

export const isLifeInsurance = (insurance: Insurance): insurance is LifeInsurance =>
  insurance.type === InsuranceType.LIFE;

export const isGenericInsurance = (insurance: Insurance): insurance is GenericInsurance =>
  insurance.type === InsuranceType.GENERIC;

export const isTravelOrRealtyOrVehicleInsurance = (
  insurance: Insurance,
): insurance is TravelInsurance | RealtyInsurance | StandardVehicleInsurance =>
  isTravelInsurance(insurance) || isRealtyInsurance(insurance) || isVehicleInsurance(insurance);

export const isLifeInsuranceContract = (contract: InsuranceContract): contract is InsuranceContract<LifeInsurance> =>
  contract.insurances.length > 0 && contract.insurances.every(isLifeInsurance);

export const groupLifeInsurancesByInsuredClient = (
  contract: InsuranceContract<LifeInsurance>,
): { client: Client; insurances: LifeInsurance[] }[] =>
  contract.clients
    .map((client, index) => ({
      client,
      insurances: contract.insurances.filter((insurance) => insurance.insuredClientIndex === index),
    }))
    .filter((group) => group.insurances.length > 0);

export const isInsuranceContract = (contract: Contract | ContractFormSearchContract): contract is InsuranceContract =>
  contract.type === ContractType.INSURANCE_CONTRACT;

export const isGenericContract = (contract: Contract | ContractFormSearchContract): contract is GenericContract =>
  contract.type === ContractType.GENERIC_CONTRACT;

export const isDepositContract = (contract: Contract | ContractFormSearchContract): contract is DepositContract =>
  contract.type === ContractType.DEPOSIT_CONTRACT;

export const isInvestmentContract = (contract: Contract | ContractFormSearchContract): contract is InvestmentContract =>
  contract.type === ContractType.INVESTMENT_CONTRACT;

export const isLoanContract = (contract: Contract | ContractFormSearchContract): contract is LoanContract =>
  contract.type === ContractType.LOAN_CONTRACT;

export const isSecondPillarContract = (
  contract: Contract | ContractFormSearchContract,
): contract is SecondPillarContract => contract.type === ContractType.SECOND_PILLAR_CONTRACT;

export const isThirdPillarContract = (
  contract: Contract | ContractFormSearchContract,
): contract is ThirdPillarContract => contract.type === ContractType.THIRD_PILLAR_CONTRACT;

export const CONTACT_DETAIL_TAB = {
  CONTRACT: "contract",
  HISTORY: "history",
  IMPORTED_HISTORY: "imported-history",
  COMMISSIONS: "commissions",
  COMPLAINTS: "complaints",
  INCREMENTS: "increments",
} as const;

export type ContactDetailTab = (typeof CONTACT_DETAIL_TAB)[keyof typeof CONTACT_DETAIL_TAB];

export const convertGainerRecordsToCreateUpdateObject = (
  gainerRecords: ContractGainerRecord[],
): CreateUpdateContractGainerRecord[] => {
  return gainerRecords.map(({ gainer1, gainer2, gainer3, gainer4, gainer5, ...data }) => {
    return {
      ...data,
      gainer1Id: gainer1.id,
      gainer2Id: gainer2?.id,
      gainer3Id: gainer3?.id,
      gainer4Id: gainer4?.id,
      gainer5Id: gainer5?.id,
    };
  });
};

export const parseClientsViolationErrors = (violations: FieldConstraintViolation[]) => {
  const parsedViolationErrors = new Map<number, FieldConstraintViolation[]>();
  violations
    .filter((violation) => violation.fieldPath.startsWith("clients"))
    .forEach((violation) => {
      const index = parseInt(violation.fieldPath.charAt(8));
      const parsedViolation = { ...violation, fieldPath: violation.fieldPath.substring(18) };
      parsedViolationErrors.set(
        index,
        parsedViolationErrors.has(index)
          ? [parsedViolation, ...(parsedViolationErrors.get(index) as FieldConstraintViolation[])]
          : [parsedViolation],
      );
    });
  return parsedViolationErrors;
};
